import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { Search, Image as ImageIcon, Check } from "lucide-react";

/**
 * MediaLibraryPicker — opens a Dialog with the admin media gallery and lets you pick image(s).
 * Props:
 *  - open, onOpenChange
 *  - multiple: false → calls onSelect(url); true → calls onSelectMany(urls[])
 *  - onSelect, onSelectMany
 */
export default function MediaLibraryPicker({ open, onOpenChange, multiple = false, onSelect, onSelectMany }) {
    const [items, setItems] = useState(null);
    const [query, setQuery] = useState("");
    const [picked, setPicked] = useState([]);

    useEffect(() => {
        if (!open) return;
        setPicked([]);
        setQuery("");
        setItems(null);
        api.get("/admin/media").then(({ data }) => setItems(data)).catch(() => setItems([]));
    }, [open]);

    const filtered = (items || []).filter((m) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return (m.original_filename || "").toLowerCase().includes(q);
    });

    const togglePick = (url) => {
        if (multiple) {
            setPicked((p) => (p.includes(url) ? p.filter((u) => u !== url) : [...p, url]));
        } else {
            onSelect?.(url);
            onOpenChange(false);
        }
    };

    const insertPicked = () => {
        onSelectMany?.(picked);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-3xl rounded-3xl max-h-[90vh] overflow-hidden flex flex-col p-0"
                data-testid="media-library-picker"
            >
                <DialogHeader className="px-6 pt-6 pb-3 border-b border-gray-100">
                    <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                        <ImageIcon className="h-5 w-5 text-brand-600" /> Media Library
                    </DialogTitle>
                    <DialogDescription>
                        {multiple
                            ? "Select one or more previously uploaded images."
                            : "Pick an image to reuse — saves a re-upload."}
                    </DialogDescription>
                    <div className="mt-3 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by filename"
                            className="pl-9 rounded-xl"
                            data-testid="media-picker-search"
                        />
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {items === null ? (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className="aspect-square rounded-2xl bg-gray-100 animate-pulse" />
                            ))}
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-16 text-ink-700">
                            {(items || []).length === 0
                                ? "No images uploaded yet. Upload from the Media tab first."
                                : "No images match your search."}
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                            {filtered.map((m) => {
                                const isPicked = picked.includes(m.public_url);
                                return (
                                    <button
                                        key={m.id}
                                        type="button"
                                        onClick={() => togglePick(m.public_url)}
                                        className={`relative aspect-square rounded-2xl overflow-hidden border-2 transition ${
                                            isPicked
                                                ? "border-brand-500 ring-2 ring-brand-200"
                                                : "border-transparent hover:border-brand-300"
                                        }`}
                                        data-testid={`media-picker-item-${m.id}`}
                                    >
                                        <img
                                            src={m.thumbnail_url || m.public_url}
                                            alt={m.original_filename || ""}
                                            className="w-full h-full object-cover"
                                        />
                                        {isPicked && (
                                            <span className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-brand-500 text-white flex items-center justify-center shadow-cta">
                                                <Check className="h-3.5 w-3.5" strokeWidth={3} />
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {multiple && (
                    <div className="border-t border-gray-100 px-6 py-3.5 flex items-center justify-between bg-paper-alt">
                        <span className="text-sm text-ink-700">
                            {picked.length} selected
                        </span>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                className="rounded-full"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                onClick={insertPicked}
                                disabled={picked.length === 0}
                                className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                                data-testid="media-picker-insert-btn"
                            >
                                Insert {picked.length > 0 ? `(${picked.length})` : ""}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
